import { LitElement, html, css } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './site-brand-lockup.js';
import './color-scheme-toggle.js';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_LABEL,
  contactTelUrl,
  contactWhatsAppUrl,
} from '../lib/site-contact';
import { contactHref, resolveRouterPath } from '../lib/router-path';
import { router } from '../router.js';

const MENU_MQ = '(max-width: 900px)';

/** Sticky chrome: secondary strip (desktop) + primary bar + mobile drawer. */
@customElement('site-header')
export class SiteHeader extends LitElement {
  @state() private menuOpen = false;

  private _mq?: MediaQueryList;

  private readonly onRoute = (): void => {
    this.menuOpen = false;
  };

  private readonly onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.menuOpen = false;
  };

  connectedCallback(): void {
    super.connectedCallback();
    router.addEventListener('route-changed', this.onRoute);
    window.addEventListener('keydown', this.onKeydown);
    this._mq = window.matchMedia(MENU_MQ);
    this._mq.addEventListener('change', this.onMqChange);
  }

  disconnectedCallback(): void {
    this.ro?.disconnect();
    this.ro = undefined;
    window.removeEventListener('resize', this.syncHeaderMeasure);
    router.removeEventListener('route-changed', this.onRoute);
    window.removeEventListener('keydown', this.onKeydown);
    this._mq?.removeEventListener('change', this.onMqChange);
    document.body.style.overflow = '';
    super.disconnectedCallback();
  }

  private ro?: ResizeObserver;

  private readonly syncHeaderMeasure = (): void => {
    const el = this.shadowRoot?.querySelector('.chrome-head');
    if (!el) return;
    const h = Math.ceil(el.getBoundingClientRect().height);
    this.style.setProperty('--site-header-stack', `${Math.max(h, 48)}px`);
  };

  firstUpdated(_changed: PropertyValues): void {
    super.firstUpdated(_changed);
    queueMicrotask(() => this.syncHeaderMeasure());
    const head = this.shadowRoot?.querySelector('.chrome-head');
    if (head) {
      this.ro = new ResizeObserver(() => this.syncHeaderMeasure());
      this.ro.observe(head);
    }
    window.addEventListener('resize', this.syncHeaderMeasure, { passive: true });
  }

  updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('menuOpen')) {
      document.body.style.overflow = this.menuOpen && this._mq?.matches ? 'hidden' : '';
      requestAnimationFrame(() => this.syncHeaderMeasure());
    }
  }

  private readonly onMqChange = (): void => {
    if (!this._mq?.matches) this.menuOpen = false;
  };

  private toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  private closeMenu(): void {
    this.menuOpen = false;
  }

  private navNavigate(): void {
    this.menuOpen = false;
  }

  static styles = css`
    :host {
      display: block;
      --site-header-stack: 4.25rem;
    }

    .chrome {
      position: sticky;
      top: 0;
      z-index: 1900;
      background: var(--bg);
    }

    .chrome-head {
      width: 100%;
    }

    .top-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem 1rem;
      box-sizing: border-box;
      width: 100%;
      max-width: var(--layout-max-width, 1120px);
      margin: 0 auto;
      padding: 0.4rem max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-left, 0px)) 0.45rem
        max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-right, 0px));
      background: var(--bg);
    }

    .top-about {
      flex-shrink: 0;
    }

    .top-nav {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-end;
      gap: 0.35rem 0.65rem;
      row-gap: 0.35rem;
      min-width: 0;
    }

    .top-sep {
      color: var(--text-muted);
      font-size: 0.75rem;
      user-select: none;
      opacity: 0.65;
    }

    .top-link {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--text-muted);
      text-decoration: none;
      white-space: nowrap;
      transition: color 0.15s ease;
    }

    .top-link:hover {
      color: var(--brand-accent-soft, #fdc06a);
    }

    .bar {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem 1rem;
      box-sizing: border-box;
      width: 100%;
      max-width: var(--layout-max-width, 1120px);
      margin: 0 auto;
      padding-top: 0.65rem;
      padding-bottom: 0.65rem;
      padding-left: max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-left, 0px));
      padding-right: max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-right, 0px));
      background: var(--bg);
    }

    .bar-end {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: flex-end;
      gap: 0.35rem;
      min-width: 0;
    }

    .nav-desktop {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem 1.1rem;
    }

    .nav-link {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--text-muted);
      text-decoration: none;
      transition: color 0.15s ease;
    }

    .nav-link:hover {
      color: var(--brand-accent-soft, #fdc06a);
    }

    .menu-toggle {
      display: none;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      padding: 0;
      margin: 0;
      border: 1px solid var(--border-card, rgba(65, 65, 65, 0.5));
      border-radius: var(--radius-sm, 4px);
      background: var(--bg-soft);
      color: var(--text);
      cursor: pointer;
      transition: border-color 0.15s ease, background 0.15s ease;
    }

    .menu-toggle:hover {
      border-color: var(--brand-accent-soft, #fdc06a);
    }

    .menu-toggle:focus-visible {
      outline: 2px solid var(--focus-visible, #4a8bc8);
      outline-offset: 2px;
    }

    .menu-toggle svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    .mobile-menu {
      display: none;
      box-sizing: border-box;
    }

    .mobile-menu.open {
      display: none;
    }

    .mobile-nav {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      gap: 0;
      min-height: 0;
    }

    .mobile-nav a {
      display: block;
      padding: 0.85rem 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--text);
      text-decoration: none;
      border-bottom: 1px solid var(--border-subtle, #343434);
      transition: color 0.15s ease;
    }

    .mobile-nav a:hover {
      color: var(--brand-accent-soft, #fdc06a);
    }

    .mobile-nav .mobile-muted {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--text-muted);
      padding: 0.65rem 0 0.25rem;
      border-bottom: none;
    }

    .backdrop {
      display: none;
      position: fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 1870;
      background: rgba(0, 0, 0, 0.45);
    }

    .backdrop.show {
      display: block;
    }

    .theme-in-bar {
      display: flex;
      align-items: center;
    }

    .theme-in-bar color-scheme-toggle {
      --toggle-size: 1.25rem;
      --toggle-icon-size: 0.56rem;
    }

    .theme-in-menu {
      display: none;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.35rem;
      padding: 0.15rem 0 0.85rem;
      border-bottom: 1px solid var(--border-subtle, #343434);
    }

    @media (max-width: 900px) {
      .top-bar {
        display: none;
      }

      .nav-desktop {
        display: none;
      }

      .menu-toggle {
        display: inline-flex;
      }

      .theme-in-bar {
        display: none !important;
      }

      .theme-in-menu {
        display: flex;
      }

      .bar {
        flex-wrap: wrap;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }

      .mobile-menu.open {
        display: flex;
        flex-direction: column;
        position: fixed;
        left: 0;
        right: 0;
        top: var(--site-header-stack, 4.25rem);
        bottom: 0;
        z-index: 1880;
        width: 100%;
        max-width: none;
        margin: 0;
        padding: 0.75rem max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-left, 0px))
          max(1.25rem, env(safe-area-inset-bottom, 0px))
          max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-right, 0px));
        background: var(--bg);
        max-height: none;
        overflow-y: auto;
      }

      .backdrop.show {
        top: var(--site-header-stack, 4.25rem);
      }
    }

    @media (min-width: 901px) {
      .mobile-menu.open {
        display: none !important;
      }

      .backdrop.show {
        display: none !important;
      }

      .theme-in-menu {
        display: none !important;
      }
    }
  `;

  render() {
    const mailQuery = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Yes We Can LLC — inquiry')}`;
    const open = this.menuOpen;
    const expanded = open ? 'true' : 'false';
    const toggleLabel = open ? 'Close menu' : 'Open menu';

    return html`
      <div class="chrome">
        <div class="chrome-head">
          <div class="top-bar">
            <a class="top-link top-about" href="${resolveRouterPath('about')}">About</a>
            <nav class="top-nav" aria-label="Contact shortcuts">
              <a class="top-link" href="${mailQuery}" title="${CONTACT_EMAIL}">Email</a>
              <span class="top-sep" aria-hidden="true">·</span>
              <a
                class="top-link"
                href="${contactWhatsAppUrl()}"
                target="_blank"
                rel="noopener noreferrer"
                >WhatsApp</a
              >
              <span class="top-sep" aria-hidden="true">·</span>
              <a class="top-link" href="${contactTelUrl()}">Call</a>
            </nav>
          </div>
          <header class="bar" aria-label="Site">
            <site-brand-lockup layout="stacked" size="md"></site-brand-lockup>
            <div class="bar-end">
              <nav class="nav-desktop" aria-label="Primary">
                <a class="nav-link" href="${resolveRouterPath('services')}">Services</a>
                <a class="nav-link" href="${resolveRouterPath('blog')}">Blog</a>
                <a class="nav-link" href="${contactHref()}">Contact</a>
              </nav>
              <button
                type="button"
                class="menu-toggle"
                aria-label="${toggleLabel}"
                aria-expanded="${expanded}"
                @click=${this.toggleMenu}
              >
                ${open
                  ? html`<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                      <path
                        d="M18.3 5.71 12 12l6.3 6.29-1.42 1.42L10.59 13.4 4.29 19.7l-1.42-1.42L9.17 12 2.88 5.71 4.3 4.29 12 11.59l7.71-7.3 1.42 1.42Z"
                      />
                    </svg>`
                  : html`<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                      <path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
                    </svg>`}
              </button>
              <span class="theme-in-bar">
                <color-scheme-toggle data-surface="page"></color-scheme-toggle>
              </span>
            </div>
          </header>
        </div>
      </div>
      <div class="mobile-menu ${open ? 'open' : ''}" id="site-mobile-menu" aria-hidden="${open ? 'false' : 'true'}">
        <nav class="mobile-nav" aria-label="Site and contact">
          <div class="theme-in-menu">
            <span class="mobile-muted">Theme</span>
            <color-scheme-toggle data-surface="page"></color-scheme-toggle>
          </div>
          <span class="mobile-muted">Contact</span>
          <a href="${mailQuery}" @click=${this.navNavigate}>Email (${CONTACT_EMAIL})</a>
          <a href="${contactWhatsAppUrl()}" target="_blank" rel="noopener noreferrer" @click=${this.navNavigate}
            >WhatsApp</a
          >
          <a href="${contactTelUrl()}" @click=${this.navNavigate}>Call ${CONTACT_PHONE_LABEL}</a>
          <span class="mobile-muted">Site</span>
          <a href="${resolveRouterPath('about')}" @click=${this.navNavigate}>About</a>
          <a href="${resolveRouterPath('services')}" @click=${this.navNavigate}>Services</a>
          <a href="${resolveRouterPath('blog')}" @click=${this.navNavigate}>Blog</a>
          <a href="${contactHref()}" @click=${this.navNavigate}>Contact page</a>
        </nav>
      </div>
      <div
        class="backdrop ${open ? 'show' : ''}"
        aria-hidden="true"
        @click=${this.closeMenu}
      ></div>
    `;
  }
}
